import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';

import { getCatalogItemsAreLoading } from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { fetchCatalogItems } from 'entities/CatalogItem/model/services/fetchCatalogItems/fetchCatalogItems';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItemsRequestData } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    getSearchCatalogItemIsActive,
    getSearchLocalCatalogItemValue,
} from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { searchCatalogItemsActions } from 'features/SearchCatalogItems/model/slices/SearchCatalogItemsSlice';
import {
    getRouteCatalog,
    getRouteCatalogInventory,
    getRouteCatalogInventoryCategories,
    getRouteInventoryAdd,
    getRouteOrdersAddItemsCategories,
    getRouteOrdersAddItemsInventory,
} from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useDebouncedCallback } from 'shared/lib/hooks/useDebouncedCallback';
import { useFetchWIthController } from 'shared/lib/hooks/useFetchWIthController';
import { SearchField } from 'shared/ui/SearchField/SearchField';

interface SearchCatalogItemsFieldProps {
    catalogId: string;
    hideBorder: boolean;
    isInventory?: boolean;
}

export const SearchCatalogItemsField = memo(
    ({
        catalogId,
        hideBorder,
        isInventory = false,
    }: SearchCatalogItemsFieldProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const fetchWithController = useFetchWIthController();

        const isPropertyCatalogSection = Boolean(
            useMatch(getRouteCatalog(':propertyId') + '/*'),
        );
        const isPropertyCatalogInventory = Boolean(
            useMatch(getRouteCatalogInventory(':propertyId') + '/*'),
        );
        const isPropertyCatalogInventoryAdd = Boolean(
            useMatch(getRouteInventoryAdd(':propertyId') + '/*'),
        );
        const isPropertyCatalogInventoryCategories = Boolean(
            useMatch(getRouteCatalogInventoryCategories(':propertyId') + '/*'),
        );

        const isOrdersAddItemsCategories = Boolean(
            useMatch(getRouteOrdersAddItemsCategories() + '/*'),
        );
        const isOrdersAddItemsInventory = Boolean(
            useMatch(getRouteOrdersAddItemsInventory() + '/*'),
        );

        const isActive = useSelector(getSearchCatalogItemIsActive);
        const isLoading = useSelector(getCatalogItemsAreLoading);
        const search = useSelector(getSearchLocalCatalogItemValue);

        const searchPlaceholder = useMemo(() => {
            const defaultSearch = `${t('Search by name')}, ${t(
                'model, SKU, or MPN',
            )}`;

            const isPropertyInventoryList =
                isPropertyCatalogInventory &&
                !isPropertyCatalogInventoryAdd &&
                !isPropertyCatalogInventoryCategories;

            const isPropertyCatalogList =
                isPropertyCatalogSection && !isPropertyCatalogInventory;

            if (isOrdersAddItemsInventory || isPropertyInventoryList) {
                return t('Search inventory');
            }

            if (isOrdersAddItemsCategories || isPropertyCatalogList) {
                return t('Search property catalog');
            }

            return defaultSearch;
        }, [
            isOrdersAddItemsCategories,
            isOrdersAddItemsInventory,
            isPropertyCatalogInventory,
            isPropertyCatalogInventoryAdd,
            isPropertyCatalogInventoryCategories,
            isPropertyCatalogSection,
            t,
        ]);

        const searchItemsDebounced = useDebouncedCallback(
            async (params: CatalogItemsRequestData) => {
                await dispatch(fetchCatalogItems(params));
            },
        );

        const onSetValue = async (searchStr: string) => {
            if (!searchStr && isActive) {
                dispatch(searchCatalogItemsActions.setActive(false));
            } else if (searchStr && !isActive) {
                dispatch(searchCatalogItemsActions.setActive(true));
            }

            if (!isLoading && searchStr) {
                dispatch(catalogItemActions.setLoading(true));
            }

            dispatch(
                searchCatalogItemsActions.setLocalCatItemSearchValue(searchStr),
            );

            if (searchStr) {
                const data = {
                    catalogId,
                    searchStr,
                    isInventory,
                };

                fetchWithController((controller: AbortController) =>
                    searchItemsDebounced({
                        data,
                        abortController: controller,
                    }),
                );
            }
        };

        return (
            <SearchField
                hideBorder={hideBorder}
                placeholder={searchPlaceholder}
                value={search}
                setValue={onSetValue}
                resettable={true}
                isActive={isActive}
            />
        );
    },
);
